import { z } from 'zod/v3';
import { GitHubBranchProtectionModeSchema } from '../generated/enums';

// GitHub auth check schema
export const CheckGitHubAuthDTOSchema = z.object({
  token: z.string(),
}).strict();

// GitHub issue state enum
export const GitHubIssueStateDTOSchema = z.enum(['open', 'closed', 'all']);

// GitHub sort direction enum
export const GitHubSortDirectionDTOSchema = z.enum(['asc', 'desc']);

// GitHub issue sort enum
export const GitHubIssueSortDTOSchema = z.enum(['created', 'updated', 'comments']);

// GitHub PR sort enum
export const GitHubPRSortDTOSchema = z.enum(['created', 'updated', 'popularity', 'long-running']);

// Base GitHub request schema
export const BaseGitHubRequestDTOSchema = z.object({
  integrationId: z.string(),
  token: z.string(),
  repoOwner: z.string(),
  repoName: z.string(),
}).strict();

// Fetch GitHub issues schema
export const FetchGitHubIssuesDTOSchema = BaseGitHubRequestDTOSchema.extend({
  state: GitHubIssueStateDTOSchema.optional(),
  labels: z.string().optional(),
  since: z.string().optional(),
  sort: GitHubIssueSortDTOSchema.optional(),
  direction: GitHubSortDirectionDTOSchema.optional(),
  limit: z.number().min(1).max(100).optional(),
}).strict();

// Fetch GitHub pull requests schema
export const FetchGitHubPullRequestsDTOSchema = BaseGitHubRequestDTOSchema.extend({
  state: GitHubIssueStateDTOSchema.optional(),
  sort: GitHubPRSortDTOSchema.optional(),
  direction: GitHubSortDirectionDTOSchema.optional(),
}).strict();

// Fetch GitHub repository content schema
export const FetchGitHubRepositoryContentDTOSchema = BaseGitHubRequestDTOSchema.extend({
  path: z.string().optional(),
  ref: z.string().optional(),
}).strict();

// GitHub document sync mode
export const GitHubSyncModeDTOSchema = z.enum(['mirror', 'collaborate', 'detach']);

export const GitHubBranchProtectionModeDTOSchema = GitHubBranchProtectionModeSchema;

export const UpdateGitHubBranchProtectionModeDTOSchema = z.object({
  repositoryId: z.string(),
  mode: GitHubBranchProtectionModeDTOSchema,
}).strict();

export const GitHubBranchProtectionModeSettingDTOSchema = z.object({
  repositoryId: z.string(),
  branchProtectionMode: GitHubBranchProtectionModeDTOSchema,
  branchProtectionModeUpdatedAt: z.date(),
}).strict();

export const ConnectedGitHubRepositoryDTOSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  name: z.string(),
  owner: z.string(),
  private: z.boolean(),
  defaultBranch: z.string(),
  syncBranch: z.string().nullable(),
  syncStatus: z.string(),
  lastSyncedAt: z.date().nullable(),
  installationId: z.string().nullable(),
  branchProtectionMode: GitHubBranchProtectionModeDTOSchema.nullable(),
  branchProtectionModeUpdatedAt: z.date().nullable(),
}).strict();

// Pull documents from GitHub schema
export const PullGitHubDocumentsDTOSchema = z.object({
  integrationId: z.string(),
  repository: z.string(),
  branch: z.string().optional(),
  path: z.string().optional(),
  targetTopicId: z.string().optional(),
}).strict();

// Push documents to GitHub schema
export const PushGitHubDocumentsDTOSchema = z.object({
  integrationId: z.string(),
  repository: z.string(),
  branch: z.string().optional(),
  basePath: z.string().optional(),
  message: z.string().optional(),
  createPR: z.boolean().optional(),
}).strict();

// GitHub repository sync configuration
export const GitHubRepoSyncConfigDTOSchema = z.object({
  repository: z.string(),
  syncPath: z.string().optional(),
  branch: z.string().optional(),
  targetTopicId: z.string().optional(),
  syncMode: GitHubSyncModeDTOSchema.optional(),
  autoSync: z.boolean().optional(),
}).strict();

// Configure GitHub document sync schema
export const ConfigureGitHubDocumentSyncDTOSchema = z.object({
  integrationId: z.string(),
  repositories: z.array(GitHubRepoSyncConfigDTOSchema),
}).strict();

// Export types
export type CheckGitHubAuthDTO = z.infer<typeof CheckGitHubAuthDTOSchema>;
export type GitHubIssueStateDTO = z.infer<typeof GitHubIssueStateDTOSchema>;
export type GitHubSortDirectionDTO = z.infer<typeof GitHubSortDirectionDTOSchema>;
export type GitHubIssueSortDTO = z.infer<typeof GitHubIssueSortDTOSchema>;
export type GitHubPRSortDTO = z.infer<typeof GitHubPRSortDTOSchema>;
export type BaseGitHubRequestDTO = z.infer<typeof BaseGitHubRequestDTOSchema>;
export type FetchGitHubIssuesDTO = z.infer<typeof FetchGitHubIssuesDTOSchema>;
export type FetchGitHubPullRequestsDTO = z.infer<typeof FetchGitHubPullRequestsDTOSchema>;
export type FetchGitHubRepositoryContentDTO = z.infer<typeof FetchGitHubRepositoryContentDTOSchema>;
export type GitHubSyncModeDTO = z.infer<typeof GitHubSyncModeDTOSchema>;
export type GitHubBranchProtectionModeDTO = z.infer<typeof GitHubBranchProtectionModeDTOSchema>;
export type UpdateGitHubBranchProtectionModeDTO = z.infer<typeof UpdateGitHubBranchProtectionModeDTOSchema>;
export type GitHubBranchProtectionModeSettingDTO = z.infer<typeof GitHubBranchProtectionModeSettingDTOSchema>;
export type ConnectedGitHubRepositoryDTO = z.infer<typeof ConnectedGitHubRepositoryDTOSchema>;
export type PullGitHubDocumentsDTO = z.infer<typeof PullGitHubDocumentsDTOSchema>;
export type PushGitHubDocumentsDTO = z.infer<typeof PushGitHubDocumentsDTOSchema>;
export type GitHubRepoSyncConfigDTO = z.infer<typeof GitHubRepoSyncConfigDTOSchema>;
export type ConfigureGitHubDocumentSyncDTO = z.infer<typeof ConfigureGitHubDocumentSyncDTOSchema>;
